import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { subscribe } from 'valtio'
import { subscribeKey } from 'valtio/utils'
import { debounce } from './utils.ts'
import { AppLanguages, languagesMap } from '../translations/messages.ts'
import { smoothScroll } from './animationUtils.ts'

export type DataState<T> = 'loading' | 'error' | T

export function isDataLoaded<T>(dataState: DataState<T>): dataState is T {
  return dataState != 'loading' && dataState != 'error'
}

const emptyStoreContext = Symbol()

export function createStoreContext<T>() {
  return createContext<T>(emptyStoreContext as never)
}

export function useStoreContext<T>(context: React.Context<T>) {
  const store = useContext(context)
  if (store === emptyStoreContext) {
    throw new Error('Component must be wrapped with StoreContext.Provider')
  }
  return store
}

export function usePromise<T>(promise: () => Promise<T>) {
  const [state, setState] = useState<DataState<T>>('loading')
  useEffect(() => {
    promise()
      .then((x) => {
        setState(x)
      })
      .catch((e) => {
        setState('error')
        return Promise.resolve(e)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return state
}

const initialRef = Symbol()
export function useLazyRef<T>(initializer: () => T) {
  const ref = useRef<T>(initialRef as never)
  if (ref.current === initialRef) {
    ref.current = initializer()
  }
  return ref
}

export function useSubscribe<T extends object>(
  proxyObject: T,
  callback: () => void,
  notifyInSync?: boolean,
) {
  useEffect(() => {
    return subscribe(proxyObject, callback, notifyInSync)
  }, [callback, notifyInSync, proxyObject])
}

export function useSubscribeKey<T extends object, K extends keyof T>(
  proxyObject: T,
  key: K,
  callback: (value: T[K]) => void,
  notifyInSync?: boolean,
) {
  useEffect(() => {
    return subscribeKey(proxyObject, key, callback, notifyInSync)
  }, [callback, key, notifyInSync, proxyObject])
}

export const MAX_MOBILE_WIDTH = 1279
export function isMobile() {
  return window.innerWidth <= MAX_MOBILE_WIDTH
}

export function useTimeout(callback: () => void, delay: number | null): void {
  const savedCallback = useRef(callback)

  useLayoutEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    if (!delay && delay !== 0) {
      return
    }

    const id = setTimeout(() => {
      savedCallback.current()
    }, delay)

    return () => {
      clearTimeout(id)
    }
  }, [delay])
}

export const useIsMobile = () => {
  const [width, setWidth] = useState(window.innerWidth)

  useLayoutEffect(() => {
    const handleWindowSizeChange = debounce(() => {
      setWidth(window.innerWidth)
    }, 300)
    window.addEventListener('resize', handleWindowSizeChange)

    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [setWidth])

  return width <= MAX_MOBILE_WIDTH
}

export function useDelay(ms: number): boolean {
  const [ready, setReady] = useState(false)
  useTimeout(() => {
    setReady(true)
  }, ms)
  return ready
}

interface UseSpotlightClickProps {
  isLastStep: boolean
  onSpotlightClick: () => void
}

// @TODO: remove this hook after joyride is removed or stop using for spotlight clicks in mobile.
export function useLastStepSpotlightClick({
  isLastStep,
  onSpotlightClick,
}: UseSpotlightClickProps) {
  const handleSpotlightClick = (event: MouseEvent) => {
    if (isLastStep) {
      const target = event.target as HTMLElement
      const spotlightElement = document.querySelector(
        '.react-joyride__spotlight',
      )
      if (
        target === spotlightElement ||
        target.firstElementChild === spotlightElement
      ) {
        onSpotlightClick()
      }
    }
  }

  useEffect(() => {
    if (isLastStep) {
      document.addEventListener('click', handleSpotlightClick)

      return () => {
        document.removeEventListener('click', handleSpotlightClick)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLastStep])
}

const isDev = () => {
  const hostname = window.location.hostname
  return (
    hostname === 'localhost' ||
    hostname.includes('dev.edman.ai') ||
    hostname.includes('staging.edman.ai')
  )
}

export function useShiftKeyLanguageSwitcher(
  setLanguage: (language: AppLanguages) => void,
  language: AppLanguages,
  interval = 1000,
) {
  useEffect(() => {
    if (!isDev()) {
      return
    }

    const shiftKeyPresses: number[] = []

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        const now = Date.now()
        shiftKeyPresses.push(now)

        while (
          shiftKeyPresses.length > 0 &&
          now - shiftKeyPresses[0] > interval
        ) {
          shiftKeyPresses.shift()
        }
        if (shiftKeyPresses.length >= 3) {
          const currentLanguage = language
          const currentIndex = languagesMap.indexOf(currentLanguage)
          const nextIndex = (currentIndex + 1) % languagesMap.length
          const newLanguage = languagesMap[nextIndex]

          setLanguage(newLanguage)
          shiftKeyPresses.length = 0
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [interval, language, setLanguage])
}

export function useSmoothScrollElement(
  scrollableElementId: string,
  condition: boolean,
) {
  useEffect(() => {
    if (!condition) return

    const scrollContainer = document.getElementById(scrollableElementId)
    if (
      scrollContainer &&
      scrollContainer.scrollWidth > scrollContainer.clientWidth
    ) {
      const initialScroll = 0
      const targetScroll = Math.min(
        40,
        scrollContainer.scrollWidth - scrollContainer.clientWidth,
      )

      setTimeout(() => {
        smoothScroll(scrollContainer, initialScroll, targetScroll, 400)

        setTimeout(() => {
          smoothScroll(scrollContainer, targetScroll, initialScroll, 400)
        }, 400)
      }, 1500)
    }
  }, [condition, scrollableElementId])
}
